import { GroupEvent } from "../types/group";

// Event times are local wall-clock values (same convention as announcement events),
// so they're parsed and shown as-is in the browser's locale.
export const formatEventRange = (event: Pick<GroupEvent, "startDateTime" | "endDateTime">): string => {
  const start = new Date(event.startDateTime);
  const end = event.endDateTime ? new Date(event.endDateTime) : null;
  const date = start.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
  const startTime = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (!end || isNaN(end.getTime())) return `${date}, ${startTime}`;

  const endTime = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (start.toDateString() === end.toDateString()) return `${date}, ${startTime} – ${endTime}`;

  const endDate = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${date}, ${startTime} – ${endDate}, ${endTime}`;
};

// Converts a server date string into the value a <input type="datetime-local"> expects.
export const toDateTimeLocal = (value?: string | null): string => (value ? value.slice(0, 16) : "");

// Rich-text editors produce "<p><br></p>" when left blank.
export const isBlankHtml = (html: string): boolean => html.replace(/<[^>]*>|&nbsp;/g, "").trim().length === 0;

// Plain-text version of an HTML description, e.g. for calendar entries.
export const stripHtml = (html?: string | null): string => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").trim();
};

export const isUpcoming = (event: GroupEvent): boolean =>
  new Date(event.endDateTime || event.startDateTime).getTime() >= Date.now();

// "2026-10-25T18:00" + 1 hour, kept in datetime-local format (local wall-clock time).
export const addOneHour = (value: string): string => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  d.setHours(d.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Same rules as the server's event endpoints: a title, a start and an end, and the end
// can't be before the start. Returns the message to show, or null when the event is valid.
export const validateEventTimes = (event: { title: string; startDateTime: string; endDateTime: string }): string | null => {
  if (!event.title.trim() || !event.startDateTime || !event.endDateTime) {
    return "Please enter a title, start and end time.";
  }
  if (new Date(event.endDateTime) < new Date(event.startDateTime)) {
    return "The end time must be after the start time.";
  }
  return null;
};
